// [S-303] Connectors hooks — Drive folder registration + sync.
// Backed by the admin endpoints in apps/admin/src/routes/connectors.ts:
//
//   GET    /tenants/:id/connectors                  (list)
//   POST   /tenants/:id/connectors/drive            (register a folder)
//   POST   /tenants/:id/connectors/drive/:cid/sync  (manual sync)
//   DELETE /tenants/:id/connectors/drive/:cid       (disconnect folder)
//   GET    /tenants/:id/connectors/drive/:cid/imported-files (history)
//   GET    /tenants/:id/connectors/drive/oauth-start (returns Google URL)
//
// The OAuth callback lives at /api/admin/connectors/drive/oauth-callback
// and is reached directly by the browser via the URL returned from
// oauth-start — we don't proxy it.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApi } from "./api";

export type ConnectorProvider = "google_drive";

export type SyncCounts = {
  imported: number;
  skippedExisting: number;
  skippedUntaggable: number;
  errored: number;
};

export type ConnectorRow = {
  connectorId: string;
  tenantId: string;
  provider: ConnectorProvider;
  folderId: string;
  displayName: string;
  connectedUserUid: string;
  connectedAt: string;
  lastSyncAt: string | null;
  lastSyncCounts: SyncCounts | null;
  lastSyncError: string | null;
};

export type ImportedFileRow = {
  driveFileId: string;
  templateId: string;
  importedAt: string;
  fileName: string;
  mimeType: string;
};

export const CONNECTORS_KEY = ["tenant", "connectors"] as const;
export const IMPORTED_FILES_KEY = ["tenant", "connectors", "imports"] as const;
export const CONNECTOR_STATUS_KEY = ["connectors", "status"] as const;

export type ConnectorStatus = {
  drive: { configured: boolean };
};

// [S-269] Is the Drive connector actually wired on this environment? The admin
// only mounts the connector routes when GOOGLE_OAUTH_* + CSAI_DRIVE_STATE_SECRET
// are set, so without this the UI can't tell "not configured" from a real error
// — the Connect button would just 404. This status route is ALWAYS mounted.
export function useDriveConnectorStatus(): UseQueryResult<
  ConnectorStatus,
  Error
> {
  const api = useApi("admin");
  return useQuery({
    queryKey: CONNECTOR_STATUS_KEY,
    queryFn: () => api<ConnectorStatus>("/connectors/status"),
  });
}

// [S-533] Mint a short-lived access token for the Google Picker. On-demand
// (only when the user opens the picker), so it's a mutation, not a query.
export function useMintDriveAccessToken(): UseMutationResult<
  { accessToken: string; expiresAt: number },
  Error,
  { tenantId: string }
> {
  const api = useApi("admin");
  return useMutation({
    mutationFn: ({ tenantId }) =>
      api<{ accessToken: string; expiresAt: number }>(
        `/tenants/${encodeURIComponent(tenantId)}/connectors/drive/access-token`
      ),
  });
}

// [S-534] Import Picker-selected files. `target` routes them to the template
// library or the deals precedent pipeline server-side.
export type DriveImportTarget = "templates" | "deals";

export type DriveImportFileOutcome = {
  fileId: string;
  name?: string;
  status: "imported" | "duplicate" | "unsupported" | "skipped_untaggable" | "error";
  ref?: string;
  error?: string;
};

export type DriveImportSummary = {
  target: DriveImportTarget;
  imported: number;
  duplicates: number;
  files: DriveImportFileOutcome[];
};

export function useDriveImport(): UseMutationResult<
  DriveImportSummary,
  Error,
  { tenantId: string; target: DriveImportTarget; fileIds: string[] }
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, target, fileIds }) =>
      api<DriveImportSummary>(
        `/tenants/${encodeURIComponent(tenantId)}/connectors/drive/import`,
        { method: "POST", body: { target, fileIds } }
      ),
    onSuccess: (_data, { tenantId }) => {
      // Deals land in ingestion; templates in the L2 library. Invalidate both
      // broadly so the relevant screens refresh after an import.
      void qc.invalidateQueries({ queryKey: ["admin", "documents"] });
      void qc.invalidateQueries({ queryKey: ["l2Templates", tenantId] });
    },
  });
}

export function useConnectors(
  tenantId: string,
  opts: { enabled?: boolean } = {}
): UseQueryResult<ConnectorRow[], Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [...CONNECTORS_KEY, tenantId] as const,
    // Gate on the connector being configured too — otherwise the list 404s on
    // an environment where Drive isn't wired (S-269).
    enabled: tenantId.length > 0 && opts.enabled !== false,
    queryFn: async () => {
      const { connectors } = await api<{ connectors: ConnectorRow[] }>(
        `/tenants/${encodeURIComponent(tenantId)}/connectors`
      );
      return connectors;
    },
  });
}

/** Starts the Drive OAuth flow. Returns the Google consent URL so the
 *  caller can redirect / open a popup. */
export function useStartDriveOAuth(): UseMutationResult<
  { authorizeUrl: string },
  Error,
  { tenantId: string }
> {
  const api = useApi("admin");
  return useMutation({
    mutationFn: ({ tenantId }) =>
      api<{ authorizeUrl: string }>(
        `/tenants/${encodeURIComponent(tenantId)}/connectors/drive/oauth-start`
      ),
  });
}

export function useRegisterDriveFolder(): UseMutationResult<
  { connector: ConnectorRow },
  Error,
  { tenantId: string; folderId: string; displayName: string }
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, folderId, displayName }) =>
      api<{ connector: ConnectorRow }>(
        `/tenants/${encodeURIComponent(tenantId)}/connectors/drive`,
        { method: "POST", body: { folderId, displayName } }
      ),
    onSuccess: (_data, { tenantId }) => {
      void qc.invalidateQueries({ queryKey: [...CONNECTORS_KEY, tenantId] });
    },
  });
}

export function useSyncDriveConnector(): UseMutationResult<
  { counts: SyncCounts; connector: ConnectorRow },
  Error,
  { tenantId: string; connectorId: string }
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, connectorId }) =>
      api<{ counts: SyncCounts; connector: ConnectorRow }>(
        `/tenants/${encodeURIComponent(tenantId)}/connectors/drive/${encodeURIComponent(connectorId)}/sync`,
        { method: "POST" }
      ),
    onSuccess: (_data, { tenantId }) => {
      void qc.invalidateQueries({ queryKey: [...CONNECTORS_KEY, tenantId] });
    },
  });
}

export function useDeleteDriveConnector(): UseMutationResult<
  void,
  Error,
  { tenantId: string; connectorId: string }
> {
  const api = useApi("admin");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, connectorId }) =>
      api<void>(
        `/tenants/${encodeURIComponent(tenantId)}/connectors/drive/${encodeURIComponent(connectorId)}`,
        { method: "DELETE" }
      ),
    onSuccess: (_data, { tenantId }) => {
      void qc.invalidateQueries({ queryKey: [...CONNECTORS_KEY, tenantId] });
    },
  });
}

export function useImportedFiles(args: {
  tenantId: string;
  connectorId: string | null;
}): UseQueryResult<ImportedFileRow[], Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: [
      ...IMPORTED_FILES_KEY,
      args.tenantId,
      args.connectorId ?? "(none)",
    ] as const,
    enabled: args.tenantId.length > 0 && args.connectorId !== null,
    queryFn: async () => {
      const { importedFiles } = await api<{ importedFiles: ImportedFileRow[] }>(
        `/tenants/${encodeURIComponent(args.tenantId)}/connectors/drive/${encodeURIComponent(args.connectorId!)}/imported-files`
      );
      return importedFiles;
    },
  });
}
